import { AfterContentInit, Directive, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, RendererStyleFlags2, SimpleChanges } from '@angular/core';
import { forkJoin } from 'rxjs';
import { EventBusService } from 'src/app/services/event-bus.service';
import { DestroyBase } from '../../../components/common/ng-destroy-base';
import { TextWidgetComponent } from '../widgets/OneReport/text-widget/text-widget.component';
import { WidgetService } from '../widgets/widget.service';
import { WidgetBaseComponent } from '../widgets/widgetBase';
import { Hashtag } from './models/hashtag';
import { HashtagPerWidget } from './models/hashtagPerWidget';
import { HashtagService } from './services/hashtag.service';

/**
 * Directive for adding hashtags and/or replacing them with correct values
 */
@Directive({
  selector: '[hashtag]',
  host: {
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()',
    '(ngModelChange)': 'onValueChanged()'
  }
})
export class HashtagDirective extends DestroyBase implements OnInit, AfterContentInit {

  /** Flag if page is in edit mode */
  @Input() editMode = false
  /** Selected tag */
  @Input() selectedTag = ''
  /** Dom Element type: currently supported: keno-editor, input and text */
  @Input() elType = 'text'
  /** Widget Id */
  @Input() widgetId: number;
  /** Page where widget is displayed */
  @Input() page: any;

  /**
   * Kendo editor value
   */
  @Input() kendoEditorValue: string;

  /**
   * Widget where directive is applied
   */
  @Input() widget: WidgetBaseComponent;

  /** Emmits new value, to update model*/
  @Output() setNewValue: EventEmitter<string> = new EventEmitter();
  /**Keeps element's value with hashtags in it */
  private valueWithHashTag: string;
  /**Page where widget is displayed */
  private pageNum: number;

  /** tracks selected hashtags per directive */
  private hashTagList: any[] = [];

  /**
   * DOM node where cursor is in kendo editor
   */
  private kendoNode = null;

  /**
   * Position of cursor in DOM node where cursor is in kendo editor
   */
  private kendoPositionInNode = 0;

  /**
   * DOM node of kendo editor
   */
  private kendoEditorDomRootNode = null;

  /**
   * Instance of kendo editor
   */
  private get kendoEditor() {
    const textWidget = this.widget as TextWidgetComponent;
    const editor = textWidget.widgetConfig.widgetSettings.showKendoEditor
      ? textWidget.editorIframe.editor
      : textWidget.editorInline.editor;

    return editor;
  }

  constructor(private el: ElementRef,
    private eventBus: EventBusService,
    private widgetService: WidgetService,
    private hashtagService: HashtagService,
    private renderer: Renderer2) {
    super();
  }

  /**
   * ngOnInit event
   */
  ngOnInit(): void {
    this.hashtagService.getAllHashtags();
    this.onEvents.push(this.eventBus.subscribeEvent('loadHashtags', (widgetId) => {
      if (widgetId === this.widget.widgetConfig?.widgetSettings.id) {
        if (!this.valueWithHashTag) {
          //TODO(SJ): second chance to get "valueWithHashTag" (if not yet set in ngAfterContentInit)
          //problem mostly occures in SWOT widget (probably textareas are bound not quick enough for hashtag directive)
          //should be ok because hashtags are not yet loaded and replaced
          this.valueWithHashTag = this.getElementValue();
        }

        this.initialHashtagLoad();
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('checkEmptyHashtags', (_event) => {
      const currentText = this.getElementValue();
      let isRed = false;
      let isYellow = false;
      const hashtags = this.getHashtags(currentText);
      hashtags.forEach(h => {
        const key = h.replace('#=', '').replace('=#', '');
        const hashtagDetails = this.hashtagService.getHashtagDto(key);
        if (hashtagDetails.value == "0" || !hashtagDetails.value) {
          isRed = true;
        } else if (hashtagDetails.value == "1") {
          isYellow = true;
        }
      });
      if (isRed) {
        this.renderer.setStyle(this.el.nativeElement, 'color', '#C70039', RendererStyleFlags2.Important);
      } else if (isYellow) {
        this.renderer.setStyle(this.el.nativeElement, 'color', '#F7BA4E', RendererStyleFlags2.Important);
      }
    }));
  }

  /**
   * ngAfterContentInit event
   */
  ngAfterContentInit(): void {
    const page = this.getPage();
    this.setPageNum(page);

    //Bug 20513: Report Design: SWOT widget, the text disappears when entering and exiting the Edit mode and when entering new text it doesn´t look right
    //TODO(SJ): value was not set yet in ngOnInit, so moved to ngAfterViewInit
    
    this.valueWithHashTag = this.getElementValue();//not set yet (for SWOT widget at least) ??
  }

  private setPageNum(page: any) {
    if (page) {
      const index = page.document?.Data?.pages?.map(p => p.options)?.indexOf(page.options);
      if (index >= 0) {
        this.pageNum = 1 + index;
      }
      else {
        this.pageNum = 1;
      }
    }
  }

  private getPage() {
    let page = this.page;
    if (!page) {
      page = this.widget.page;
    }
    return page;
  }

  private initialHashtagLoad() {
    const currentText = this.getElementValue();
    const hashtags = this.getHashtags(currentText);
    // if there are already selected hashtags, add them to the hashtag list
    hashtags.forEach(h => {
      const key = h.replace('#=', '').replace('=#', '');
      this.hashTagList.push(key);
    })

    // load values for all hashtags
    const hashtagObservables = this.hashtagService.createHashtagDetailsObservables(hashtags, this.pageNum, this.widgetId);
    const page = this.getPage();
    if (hashtagObservables && hashtagObservables.length > 0) {
      forkJoin(hashtagObservables).subscribe(() => {
        //if page is not in edit mode on initial load, replace hashtags with real values
        if (!this.page?.editMode) {
          this.replaceHashTagWithValue();
        }

        //TODO(SJ): this is called when first occurance of hashtag in widget is resolved, what about others??
        page?.widgetDataLoaded(this.widget.widgetConfig?.widgetSettings.id);
      });
    } else {
      //TODO(SJ): this is called when first occurance of hashtag in widget is resolved, what about others??
      page?.widgetDataLoaded(this.widget.widgetConfig?.widgetSettings.id);
    }
  }

  /**Tracks changes for editMode and selectedTag*/
  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedTag) {
      // If selected tag is changed, add tag to current value
      if (this.selectedTag) {
        this.addHashTagToElement();
      }
    } else if (changes.editMode) {
      // If edit mode is changed, check if hashtag should be replaced with real value
      this.replaceHashTagWithValue();
    }
  }

  /**
   * Kendo event handler
   * Events are sent from kendo editor
   */
  handleKendoEvent(kendoEvent: string): void {
    switch (kendoEvent) {
      case "focus":
        this.onFocus();
        break;
      case "blur":
        this.onBlur();//remark: blur doesn't work for kendo editor
        break;
      case "onValueChanged":
        this.onValueChanged();
        break;
    }

    this.calculateCurrentKendoCursorPosition();
  }

  /**
   * Returns list of hashtags in text content
   */
  private getHashtags(content: string): string[] {
    return content.match(/#=\w+=#/g) || [];
  }

  /**
   * Checks if event should be handled (is it meant for this directive)
   */
  private shouldhandleEvent(): boolean {
    const elInFocus = this.widgetService.getFocusedElementId();
    const itIsMe = elInFocus === this.el.nativeElement.id;
    return itIsMe;
  }

  /**
   * Calculate position of cursor in kendo editor
   */
  private calculateCurrentKendoCursorPosition() {
    this.kendoEditorDomRootNode = null;
    const textWidget = this.widget as TextWidgetComponent;
    let selectionDocument;
    if (textWidget.widgetConfig.widgetSettings.showKendoEditor) {
      const iframe = this.el.nativeElement.querySelector('iframe');
      this.kendoEditorDomRootNode = iframe.contentWindow.document.body;
      selectionDocument = iframe.contentWindow.document;
    } else {
      this.kendoEditorDomRootNode = this.el.nativeElement.querySelector('.k-editor-content');
      selectionDocument = document;
    }

    if (this.kendoEditorDomRootNode) {
      const selection = selectionDocument.getSelection();
      if (selection.rangeCount > 0) {
        const _range = selection.getRangeAt(0);
        const range = _range.cloneRange();
        range.selectNodeContents(this.kendoEditorDomRootNode);
        range.setEnd(_range.endContainer, _range.endOffset);

        this.kendoPositionInNode = _range.endOffset;
        this.kendoNode = _range.endContainer;
        if (this.kendoNode.nodeValue === null && this.kendoNode.childNodes.length > this.kendoPositionInNode) {
          this.kendoNode = this.kendoNode.childNodes[this.kendoPositionInNode];
          this.kendoPositionInNode = 0;
        }
      }
    }
  }

  /**
   * Returns position of hashtag start marker in content
   */
  private getPositionOfHashtagStartMarker(content: string): number {
    const hashtags = this.getHashtags(content);
    //work on a copy of content
    let textCopy = content;
    //replace all hashtags with underline string (keep same length, so we preserve nodeText legnt)
    for (const hashtag of hashtags) {
      textCopy = textCopy.replace(hashtag, ''.padEnd(hashtag.length, '_'));
    }

    return textCopy.indexOf('#=');
  }

  /**
   * Add hashtag to text content
   */
  addHashTagToElement() {
    //If there are multiple inputs with hashtag, latest hashtag should only be added to the el that is in focus
    if (this.selectedTag && this.shouldhandleEvent()) {
      let currentText;
      const hashtagMarker = `#=${this.selectedTag}=#`;

      if (this.elType !== "kendo") {
        const cursorPosition = this.el.nativeElement.selectionStart;
        currentText = this.getNodeTextNonKendo(hashtagMarker, cursorPosition);
      } else {
        if (!this.kendoNode) {
          //no kendo editor (node), so return: probably called from ngOnChanges (change of selectedTag)
          //what is the purpose of this selectedTag input anyway??
          return;
        }

        // we kept track of last selected node and position (see: calculateCurrentKendoCursorPosition), so use these
        const node = this.kendoNode;
        const cursorPosition = this.kendoPositionInNode;
        const nodeText: string = this.getNodeText(hashtagMarker, cursorPosition);

        this.setNodeValue(node, nodeText);

        const newValue = this.kendoEditor.value;
        currentText = newValue;
      }

      // set new value to DOM el
      this.setElementValue(currentText);

      // emit new value, in order to update model and trigger dirty flag
      if (this.setNewValue) {
        this.setNewValue.emit(currentText);
      }

      this.valueWithHashTag = currentText;
      this.setHashtagValue(this.selectedTag);
      const dummyHashtag = new HashtagPerWidget();
      dummyHashtag.widgetId = this.widgetId;
      dummyHashtag.hashtag = new Hashtag();
      dummyHashtag.hashtag.key = '';
      this.hashtagService.applyHashtagToWidget(dummyHashtag);
    }
  }

  private getNodeTextNonKendo(hashtagMarker: string, cursorPosition: any) {
    let currentText = this.getElementValue();

    const openHashtag = currentText.match(/#=(?![^#\s]*=#)\w+/g) || [];
    if (openHashtag.length > 0) {
      currentText = currentText.replace(openHashtag[0], hashtagMarker);
    } else {
      const hashtagStartMarkerIndex = this.getPositionOfHashtagStartMarker(currentText);
      if (hashtagStartMarkerIndex !== -1) {
        //we found occurance of #=, so replace it with hashtag
        currentText = currentText.slice(0, hashtagStartMarkerIndex) + hashtagMarker + currentText.slice(hashtagStartMarkerIndex + 2);
      } else if (cursorPosition) {
        currentText = currentText.slice(0, cursorPosition) + hashtagMarker + currentText.slice(cursorPosition);
      } else {
        currentText = `${currentText} ${hashtagMarker}`;
      }
    }
    return currentText;
  }

  private getNodeText(hashtagMarker: string, cursorPosition: number) {
    let nodeText: string = this.kendoNode.textContent;

    //we will use exec instead of match, so we get index of found open hashtag
    const regExp = /#=(?![^#\s]*=#)\w+/g;
    const openHashtags: RegExpExecArray = regExp.exec(nodeText);
    if (openHashtags?.length > 0) {
      const matchedOpenHashtag = openHashtags[0];
      //for backwards compatibility we won't check if open hashtag is (starts with) really one we are setting => just replace
      const matchIndex = openHashtags.index;
      nodeText = nodeText.slice(0, matchIndex) + hashtagMarker + nodeText.slice(matchIndex + matchedOpenHashtag.length);
    } else {
      const hashtagStartMarkerIndex = this.getPositionOfHashtagStartMarker(nodeText);
      if (hashtagStartMarkerIndex !== -1) {
        //we found occurance of #=, so replace it with hashtag
        nodeText = nodeText.slice(0, hashtagStartMarkerIndex) + hashtagMarker + nodeText.slice(hashtagStartMarkerIndex + 2);
      } else {
        nodeText = nodeText.slice(0, cursorPosition) + hashtagMarker + nodeText.slice(cursorPosition);
      }
    }

    return nodeText;
  }

  private setNodeValue(node: any, nodeText: string) {
    if (node.nodeType === 3) {
      //text node
      node.nodeValue = nodeText;
    } else {
      //if not text node => insert text node before this node
      node.insertAdjacentText('beforebegin', nodeText);
    }
  }

  /**
   * Replace hashtag with real value
   */
  replaceHashTagWithValue() {
    let currentText = this.getElementValue();

    // if page is not in edit mode, replace hashtags with real values
    if (!this.editMode) {
      this.valueWithHashTag = currentText;
      const hashtags = this.getHashtags(currentText);
      hashtags.forEach(h => {
        const key = h.replace('#=', '').replace('=#', '');
        const hashtagDetails = this.hashtagService.getHashtagDto(key);
        if (hashtagDetails?.value !== null && hashtagDetails?.value !== undefined) {
          const hashtagValue = this.hashtagService.formatHashtagValue(hashtagDetails.value, hashtagDetails);
          currentText = currentText.replace(h, hashtagValue);
          this.setElementValue(currentText);
        }
        else { // otherwise replace hashtag with empty value
          currentText = currentText.replace(h, '');
          this.setElementValue(currentText);
        }
      });
      // emit new value, in order to update model and trigger dirty flag
      if (this.setNewValue) {
        this.setNewValue.emit(currentText);
      }
    } else {
      // otherwise, revert values to use hashtags
      this.setElementValue(this.valueWithHashTag);
      // emit new value, in order to update model and trigger dirty flag
      if (this.setNewValue) {
        this.setNewValue.emit(this.valueWithHashTag);
      }
    }
  }

  //#region Host Listeners

  /**Save focused element id, in order to know where to apply hashtag */
  onFocus() {
    this.widgetService.setFocusedElementId(this.el.nativeElement.id);
  }

  /**Check if any tags were deleted */
  onBlur() {
    const currentText = this.getElementValue();
    const activedHashTags = this.getHashtags(currentText);
    const temphashtagList = [];
    // iterate through saved hashtags and check if all hashtags are still active
    this.hashTagList.forEach(h => {
      const hashtag = activedHashTags.find(ht => ht.replace('#=', '').replace('=#', '') === h);
      if (!hashtag) {
        this.hashtagService.removeSelectedHashtag(this.widgetId, h, this.pageNum)
      } else {
        activedHashTags.splice(activedHashTags.indexOf(hashtag), 1);
        temphashtagList.push(h);
      }
    });
    this.hashTagList = temphashtagList;
  }

  private replaceHtml(str) {
    return str.replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
  }

  /** Check if user has enetered search for hashtags (#=...) */
  onValueChanged() {
    const value = this.getElementValue();
    let eneteredText = this.replaceHtml(value);
    const hashtags = this.getHashtags(eneteredText);

    //remove all hashtags from content
    hashtags.forEach(h => {
      eneteredText = eneteredText.replace(h, '');
    });

    //when all is removed, see if there is something like #=bla
    const searchKey = eneteredText.match(/#=\w+/g);
    if (searchKey) {
      const key = searchKey[0].replace('#=', '').trim();
      this.hashtagService.setSearchKey(key);
      this.eventBus.broadcastEvent('displayHashTagsTab', this.widget);
    } else if (eneteredText.indexOf('#=') !== -1) {
      //user just entered #= (starting to type some hashtag)
      //so also open sidebar with hashtags with all hashtags displayed
      this.eventBus.broadcastEvent('displayHashTagsTab', this.widget);
      this.hashtagService.setSearchKey(null);
    } else {
      this.hashtagService.setSearchKey(null);
    }
  }

  //#endregion End Host Listeners

  /**
   * Sets the hashtag value
   */
  private setHashtagValue(key) {
    this.hashtagService.getHashtagDtoDetails(key).subscribe(hashtagDetails => {
      if (hashtagDetails) {
        this.handleHashtagValue(key, hashtagDetails);
      }
    });
  }

  private handleHashtagValue(key: any, hashtagDetails: Hashtag) {
    this.hashtagService.setHashtagDetails(key, hashtagDetails);
    const property = hashtagDetails.property;
    // if details were not loaded, load them
    if (!hashtagDetails.isValueLoaded) {
      this.subscriptions.add(this.hashtagService.getHashtagValue(key).subscribe(data => {
        //if selected hashtag has value apply it
        this.applyHashtagValue(data, property, hashtagDetails, key);
      }));
    }
  }

  private applyHashtagValue(data: any, property: string, hashtagDetails: Hashtag, key: any) {
    let hashtagValue: string;
    if (data[property] !== undefined && data[property] !== null) {
      hashtagValue = data[property];
      // if hastag has reference to ref list, call api for ref list
      if (hashtagDetails.reflist_name) {
        this.subscriptions.add(this.widgetService.loadReflist(hashtagDetails.reflist_name).subscribe((data: Array<any>) => {
          const refListSelectedValue = data.find(d => d.oid === +hashtagValue);
          if (refListSelectedValue) {
            hashtagValue = refListSelectedValue.description;
            this.hashtagService.setHashtagValue(key, hashtagValue);
            hashtagDetails.value = hashtagValue;

            this.hashtagService.addHashtagToReportList(hashtagDetails, this.pageNum, this.widgetId);
            this.hashtagService.clearSelectedHashtag();
          }
        }));
      }
    }
    if (!hashtagDetails.reflist_name) {
      if((hashtagValue === undefined || hashtagValue === null) && hashtagDetails.secondaryField !== undefined){
        hashtagValue = data[hashtagDetails.secondaryField];
      }
      this.hashtagService.setHashtagValue(key, hashtagValue);
      hashtagDetails.value = hashtagValue;

      this.hashtagService.addHashtagToReportList(hashtagDetails, this.pageNum, this.widgetId);
      this.hashtagService.clearSelectedHashtag();
    }
    return hashtagValue;
  }

  /**
   * Sets the value to hashtag container (kendo editor or input element)
   */
  private setElementValue(newValue: string) {
    switch (this.elType) {
      case 'kendo':
        if (this.kendoEditor) {
          //TODO: kendoEditor is sometimes not set yet!?!
          this.kendoEditor.value = newValue;
        }
        break;
      case 'text':
        this.el.nativeElement.value = newValue;
        break;
    }
  }

  /**
   * Gets the value to hashtag container (kendo editor or input element)
   */
  private getElementValue() {
    switch (this.elType) {
      case 'kendo':
        return this.kendoEditorValue;
      case 'text':
        return this.el.nativeElement.value;
      default:
        return '';
    }
  }
}
